import {
  buildBrandingGalleryPayload,
  findInvalidBrandingGalleryItems,
  isBrandingGallerySendableItem,
  pickBrandingGalleryFromClub,
  BRANDING_GALLERY_MAX,
} from "../config/clubWebsitePages";
import { getCloudinaryMediaBaseUrl } from "../config/cloudinaryBase";
import { resolveCloudinaryImageSrc, uploadFile } from "./helper.service";
import { updateClubWebsite } from "../api/clubApi";

/** @param {File} file */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Read failed"));
    reader.readAsDataURL(file);
  });
}

async function blobUrlToBase64(blobUrl) {
  const res = await fetch(blobUrl);
  const blob = await res.blob();
  return fileToBase64(
    new File([blob], "gallery.jpg", { type: blob.type || "image/jpeg" })
  );
}

/** Normalize API/state items to https or data:image (never relative paths). */
export function normalizeBrandingGalleryForState(gallery) {
  if (!Array.isArray(gallery)) return [];
  const base = getCloudinaryMediaBaseUrl();
  return gallery
    .map((item) => {
      let s = "";
      if (typeof item === "string") s = item.trim();
      else if (item && typeof item === "object" && item.url != null) {
        s = String(item.url).trim();
      }
      if (!s || /^blob:/i.test(s)) return null;
      if (/^data:image\//i.test(s) || /^https?:\/\//i.test(s)) return s;
      return resolveCloudinaryImageSrc(s, base, s);
    })
    .filter(Boolean)
    .slice(0, BRANDING_GALLERY_MAX);
}

/** Existing https URLs unchanged; relative Cloudinary paths → full https; base64 kept. */
function normalizeGalleryItemForPut(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (/^data:image\//i.test(s)) return s;
  if (/^https?:\/\//i.test(s)) return s;
  return resolveCloudinaryImageSrc(s, getCloudinaryMediaBaseUrl(), s);
}

/**
 * Prepare gallery for PUT: no blob: URLs; https + data:image/... only.
 * @param {string[]} gallery
 * @returns {Promise<string[]>}
 */
export async function prepareBrandingGalleryForSave(gallery) {
  if (!Array.isArray(gallery)) return [];
  const out = [];
  for (let i = 0; i < gallery.length; i += 1) {
    let s = String(gallery[i] ?? "").trim();
    if (!s) continue;
    if (/^blob:/i.test(s)) {
      s = await blobUrlToBase64(s);
    } else {
      s = normalizeGalleryItemForPut(s);
    }
    if (!isBrandingGallerySendableItem(s)) {
      throw new Error("GALLERY_INVALID_ITEM");
    }
    out.push(s);
  }
  const invalid = findInvalidBrandingGalleryItems(out);
  if (invalid.length > 0) {
    throw new Error("GALLERY_BLOB_INVALID");
  }
  return buildBrandingGalleryPayload(out);
}

/**
 * PUT /club/website/:clubId — backend persists brandinggallery here.
 * @param {string} clubId
 * @param {string[]} gallery
 * @param {{ alreadyResolved?: boolean, extra?: Record<string, unknown> }} [options]
 */
export async function persistBrandingGalleryViaWebsite(clubId, gallery, options = {}) {
  const id = String(clubId ?? "").trim();
  if (!id) throw new Error("Missing club id");
  const brandinggallery = options.alreadyResolved
    ? buildBrandingGalleryPayload(gallery)
    : await prepareBrandingGalleryForSave(gallery);
  const body = { brandinggallery, ...(options.extra || {}) };
  return updateClubWebsite(id, body);
}

/** Upload new local files to Cloudinary; keep https + existing data URLs. */
export async function uploadNewGalleryFilesToCloudinary(gallery) {
  if (!Array.isArray(gallery)) return [];
  const out = [];
  for (let i = 0; i < gallery.length; i += 1) {
    let s = String(gallery[i] ?? "").trim();
    if (!s) continue;
    if (/^blob:/i.test(s)) {
      s = await blobUrlToBase64(s);
    }
    if (/^data:image\//i.test(s)) {
      try {
        const res = await fetch(s);
        const blob = await res.blob();
        const ext = (blob.type || "image/jpeg").split("/")[1] || "jpg";
        const file = new File([blob], `gallery.${ext}`, {
          type: blob.type || "image/jpeg",
        });
        const uploaded = await uploadFile(file);
        s = normalizeGalleryItemForPut(uploaded);
      } catch {
        /* keep base64 if Cloudinary fails — backend may accept it */
      }
    } else {
      s = normalizeGalleryItemForPut(s);
    }
    if (isBrandingGallerySendableItem(s)) out.push(s);
  }
  return buildBrandingGalleryPayload(out);
}

/** @param {unknown} payload API response or club doc */
export function pickBrandingGalleryFromApiPayload(payload) {
  if (!payload || typeof payload !== "object") return [];
  const fromRoot = pickBrandingGalleryFromClub(payload);
  if (fromRoot.length > 0) return normalizeBrandingGalleryForState(fromRoot);
  const data = payload.data;
  if (data && typeof data === "object") {
    return normalizeBrandingGalleryForState(pickBrandingGalleryFromClub(data));
  }
  return [];
}

/** @param {unknown} payload create/update response */
export function extractClubIdFromPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  const raw = payload.data ?? payload;
  if (!raw || typeof raw !== "object") return null;
  const id = raw._id ?? raw.id;
  return id != null ? String(id) : null;
}
